import { useRouter as usePagesRouterSafe } from "next/compat/router"
import { useParams, usePathname, useSearchParams } from "next/navigation"
import { useMemo } from "react"

interface AgnosticRouterInfo {
  asPath: string
  pathname: string
  query: Record<string, string>
  isAppRouter: boolean
}

export function useAgnosticRouterInfo(): AgnosticRouterInfo {
  // Always call hooks at the top level
  const pagesRouter = usePagesRouterSafe()
  const appPathname = usePathname()
  const appSearchParams = useSearchParams()
  const appParams = useParams()

  const isAppRouter = pagesRouter === null

  return useMemo(() => {
    if (isAppRouter) {
      // App Router implementation
      const query = {
        ...Object.fromEntries(appSearchParams?.entries() ?? []),
        ...(appParams as Record<string, string>),
      }

      const searchParamsString = appSearchParams?.toString()
      const fullPath = appPathname
        ? appPathname + (searchParamsString ? `?${searchParamsString}` : "")
        : ""

      return {
        asPath: fullPath,
        isAppRouter: true,
        pathname: appPathname ?? "",
        query,
      }
    } else {
      // Pages Router implementation
      return {
        asPath: pagesRouter?.asPath ?? "",
        isAppRouter: false,
        pathname: pagesRouter?.pathname ?? "",
        query: (pagesRouter?.query as Record<string, string>) ?? {},
      }
    }
  }, [isAppRouter, appPathname, appSearchParams, appParams, pagesRouter])
}
